'use client'

// Precompiler — builds GPU pipelines before anything needs them drawn.
//
// The webgpu backend builds a material's pipeline the first time an object
// wearing it is drawn, which is the first time it is inside the frustum.
// Building one is two costs, both inside that frame: three turns the TSL
// graph into a shader on the main thread (~20 ms a material on an M4 Pro),
// then Metal compiles it on the GPU process's main thread (up to 300 ms for
// one, and nothing on the page presents meanwhile). Behind the veil only
// what the camera saw at spawn was built, so the first turn built the rest:
// one full circle after the veil made 35 pipelines and lost half a second
// (docs/world-performance.md, 28 Oct). That is the stutter Sam felt in the
// first seconds of walking around.
//
// Two jobs, one door:
//
// 1. Boot. Once the models are in and the first world state has landed (so
//    the food, the offerings, the bird are mounted), everything in the
//    scene is compiled in one compileAsync, un-culled, hidden things
//    shown for the call, and the veil waits for it (worldCompiled). compileAsync builds through createRenderPipelineAsync,
//    so the Metal compiles run on worker threads, not the GPU main thread.
//    ~0.7 s behind the veil on an M4 Pro; the same turn then builds 1
//    pipeline and drops no frame.
//
// 2. Late arrivals. Every second, anything shown whose material has not
//    been compiled at its current version (a new offering's crystal, a
//    poster hung by the poll, a print, a sprayed wall, a serving on the
//    counter) is compiled the same way, before it is looked at.
//
// The target is the part that was wrong before. compileAsync builds
// against renderer._renderTarget, which is null when drawing to the
// screen, but a real frame with tone mapping or sRGB output draws into
// three's internal frame-buffer target (half float, the canvas's MSAA): a
// different render context, so different pipelines. The old sweep built 73
// pipelines that no frame ever used, and the turn still built its 35.
// compileForFrame sets that target for the call. It is also why the first
// whole-scene attempt turned the world black: the lake's depth copy was
// allocated for the bare context, at sample count 1 against a 4x canvas.
// Against the real target the counts match, but the lake's depth copy
// still runs inside compileAsync and ends a pass that is not open, so
// anything like it is marked userData.compileInFrame: left out of
// compileAsync and drawn un-culled in one real frame behind the veil,
// which builds it the ordinary way where nobody can see. Anything else
// that misbehaves can opt out with userData.noPrecompile.
//
// During each call every object not being compiled is hidden, and the
// compiled ones are made un-cullable; both are restored before the call
// returns, because compileAsync does all of its walking and issuing
// synchronously and only awaits the GPU at the very end (three r182), so
// no real frame ever sees the changed flags.
//
// Not covered: shadow-pass variants (built in the shadow pass itself; the
// shadow camera covers the whole town, so the town's are built on frame
// one), and the post-processing pass's own render context while a trip is
// on (it renders to its own target; rare, and only then).

import { useEffect, useRef } from 'react'
import { useThree } from '@react-three/fiber'
import { useProgress } from '@react-three/drei'
import useGameStore from '../../../stores/gameStore'
import { ditherMaterial } from './ditherRegistry'

// A timer, not a frame count. Fresh materials are rare (a poll, a new
// offering), so most sweeps find nothing and the traverse is their only
// cost; and a timer keeps working while the tab is hidden or the page is
// only painting on demand, so an offering that lands while a visitor is
// in another tab is compiled by the time they come back.
const EVERY_MS = 1000

// How long to wait for the first world state once the models are in,
// before compiling without it. The veil itself waits up to a minute for a
// slow town; the town's own materials should not wait that long.
const SKY_GRACE_MS = 5000

function materialsOf(o) {
  const m = o.material
  if (!m) return null
  return Array.isArray(m) ? m : [m]
}

const leftOut = (o) => o.userData?.noPrecompile || o.userData?.compileInFrame

function shown(o) {
  for (let p = o; p; p = p.parent) if (!p.visible) return false
  return true
}

/**
 * Draw nothing whose pipeline is still being built. Compiling against the
 * real target means a real frame finds the same pipeline compileAsync is
 * still building, and three r182's WebGPUBackend.draw hands its undefined
 * handle to setPipeline, which throws mid-pass and leaves the pass open
 * ("already ended" on every draw after). Skipped instead, the thing
 * appears a frame or two later, when its pipeline lands, rather than the
 * frame stalling for it. Called once, from the renderer's setup.
 */
export function holdUnreadyDraws(renderer) {
  const backend = renderer.backend
  if (!backend?.isWebGPUBackend || typeof backend.draw !== 'function') return
  const draw = backend.draw
  backend.draw = function (renderObject, info) {
    const data = this.get(renderObject.pipeline)
    if (data.pipeline === undefined && data.error !== true) return
    return draw.call(this, renderObject, info)
  }
}

/** compileAsync against the target a real frame draws into (see above). */
export function compileForFrame(gl, scene, camera) {
  const prev = gl.getRenderTarget()
  const fb = prev === null && typeof gl._getFrameBufferTarget === 'function' ? gl._getFrameBufferTarget() : null
  if (fb) gl.setRenderTarget(fb)
  try {
    return gl.compileAsync(scene, camera)
  } finally {
    if (fb) gl.setRenderTarget(prev)
  }
}

export function Precompiler() {
  const { gl, scene, camera } = useThree()
  const seen = useRef(new WeakMap())   // material -> version it was compiled at
  const busy = useRef(false)
  const booted = useRef(false)
  const { active, progress } = useProgress()
  const assetsDone = !active && progress >= 100
  const skySettled = useGameStore((s) => s.skySettled)
  const able = typeof gl?.compileAsync === 'function'

  function sweep(boot) {
    if (busy.current) return Promise.resolve()

    const fresh = []
    const others = []
    // Boot also compiles what is hidden until something happens (the
    // meadow until you are near it, rain, a granted want, ripe fruit), so
    // its first appearance is not a compile either: everything is shown
    // for the call and put back after.
    const revealed = []
    scene.traverse((o) => {
      if (boot && !o.visible && !leftOut(o)) { revealed.push(o); o.visible = true }
      const mats = materialsOf(o)
      if (!mats) return
      if (leftOut(o) || !shown(o)) { others.push(o); return }
      if (!boot && mats.every((m) => seen.current.get(m) === m.version)) { others.push(o); return }
      fresh.push(o)
    })
    if (fresh.length === 0) {
      for (const o of revealed) o.visible = false
      return Promise.resolve()
    }

    // The dither goes on before the compile, or the compile builds the
    // clean shader and the patch makes it build again.
    for (const o of fresh) for (const m of materialsOf(o)) ditherMaterial(m)

    busy.current = true
    const culled = fresh.map((o) => o.frustumCulled)
    const visible = others.map((o) => o.visible)
    for (const o of fresh) o.frustumCulled = false
    for (const o of others) o.visible = false
    let p
    try {
      p = compileForFrame(gl, scene, camera)
    } catch (err) {
      console.warn('[precompile] compileAsync threw:', err?.message || err)
      p = Promise.resolve()
    } finally {
      fresh.forEach((o, i) => { o.frustumCulled = culled[i] })
      others.forEach((o, i) => { o.visible = visible[i] })
      for (const o of revealed) o.visible = false
    }
    // Marked now, so a material that fails to build is not retried every
    // second forever.
    for (const o of fresh) for (const m of materialsOf(o)) seen.current.set(m, m.version)
    return Promise.resolve(p)
      .catch((err) => console.warn('[precompile] pipeline build failed:', err?.message || err))
      .finally(() => { busy.current = false })
  }

  // Boot: models in, first world state landed (or the grace ran out), then
  // two frames so the materials models convert in effects are in place.
  useEffect(() => {
    if (booted.current || !able || !assetsDone) return undefined
    let raf = 0
    const go = () => {
      if (booted.current) return
      booted.current = true
      const t0 = performance.now()
      const inFrame = []
      scene.traverse((o) => { if (o.userData?.compileInFrame && o.material) inFrame.push([o, o.frustumCulled]) })
      for (const [o] of inFrame) o.frustumCulled = false
      sweep(true).finally(() => {
        // a few real frames have drawn the in-frame ones by the time this
        // lands; two more to be sure, then they are culled as before
        let n = 0
        const tick = () => {
          if (++n < 3) { requestAnimationFrame(tick); return }
          for (const [o, f] of inFrame) o.frustumCulled = f
          useGameStore.getState().setWorldCompiled(true)
          console.log(`[precompile] town compiled in ${Math.round(performance.now() - t0)} ms`)
        }
        requestAnimationFrame(tick)
      })
    }
    const afterTwoFrames = () => {
      let n = 0
      const tick = () => { if (++n >= 2) go(); else raf = requestAnimationFrame(tick) }
      raf = requestAnimationFrame(tick)
    }
    if (skySettled) { afterTwoFrames(); return () => cancelAnimationFrame(raf) }
    const t = setTimeout(afterTwoFrames, SKY_GRACE_MS)
    return () => { clearTimeout(t); cancelAnimationFrame(raf) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [able, assetsDone, skySettled])

  // On the webgl fallback without compileAsync there is nothing to wait for.
  useEffect(() => {
    if (!able) useGameStore.getState().setWorldCompiled(true)
  }, [able])

  // Late arrivals.
  useEffect(() => {
    if (!able) return undefined
    const id = setInterval(() => { if (booted.current) sweep(false) }, EVERY_MS)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [able, gl, scene, camera])

  return null
}

export default Precompiler
